import { CheckCircle2, SlidersHorizontal, WandSparkles } from "lucide-react";
import { BrandMark } from "@/components/BrandLogo";
import { themePreviewStyle, type ThemeColors, type ThemeMode } from "@/lib/theme";

interface ThemePreviewProps {
  mode: ThemeMode;
  colors: ThemeColors;
}

export function ThemePreview({ mode, colors }: ThemePreviewProps) {
  return (
    <div className="lg:sticky lg:top-6">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-studio-ink">Live preview</p>
          <p className="mt-0.5 text-xs text-studio-muted">Your workspace changes only after saving.</p>
        </div>
        <span className="rounded-full bg-studio-card px-2.5 py-1 font-mono text-[10px] font-medium uppercase text-studio-muted">
          {mode}
        </span>
      </div>

      <div
        aria-label={`${mode} theme preview`}
        style={themePreviewStyle(mode, colors)}
        className="overflow-hidden rounded-xl border border-studio-border bg-studio-bg shadow-panel"
      >
        <div className="flex h-9 items-center gap-1.5 border-b border-studio-border bg-studio-panel px-3">
          <span className="h-2 w-2 rounded-full bg-studio-danger" />
          <span className="h-2 w-2 rounded-full bg-studio-warning" />
          <span className="h-2 w-2 rounded-full bg-studio-success" />
          <span className="ml-2 font-mono text-[8px] text-studio-muted">studio.local</span>
        </div>

        <div className="flex min-h-[340px] sm:min-h-[390px]">
          <aside className="w-[31%] border-r border-studio-border bg-studio-panel p-3">
            <div className="mb-5 flex items-center gap-2">
              <BrandMark className="h-6 w-6 rounded-md" />
              <span className="truncate text-[9px] font-bold text-studio-ink">PandaFilms</span>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 rounded-md bg-studio-accent-soft px-2 py-2 text-studio-accent">
                <WandSparkles className="h-3 w-3" />
                <span className="text-[8px] font-semibold">Studio</span>
              </div>
              {["Gallery", "Library", "Projects"].map((item) => (
                <div key={item} className="flex items-center gap-2 rounded-md px-2 py-2 text-studio-muted">
                  <span className="h-3 w-3 rounded-sm border border-studio-border-light" />
                  <span className="text-[8px]">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 border-t border-studio-border pt-3">
              <div className="h-1.5 w-3/4 rounded-full bg-studio-card-hover" />
              <div className="mt-2 h-1.5 w-1/2 rounded-full bg-studio-border" />
            </div>
          </aside>

          <div className="min-w-0 flex-1 p-4 sm:p-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-[8px] font-semibold text-studio-accent">IMAGE WORKFLOW</p>
                <h3 className="mt-1 text-sm font-semibold text-studio-ink">Create an image</h3>
                <p className="mt-1 text-[8px] text-studio-muted">Describe the result, then choose output settings.</p>
              </div>
              <span className="flex h-7 w-7 items-center justify-center rounded-md border border-studio-border bg-studio-card text-studio-muted">
                <SlidersHorizontal className="h-3 w-3" />
              </span>
            </div>

            <div className="rounded-lg border border-studio-border bg-studio-panel p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[8px] font-semibold text-studio-ink">Creative prompt</span>
                <span className="font-mono text-[7px] text-studio-muted">0 / 4000</span>
              </div>
              <div className="h-16 rounded-md border border-studio-border bg-studio-card p-2">
                <span className="text-[8px] leading-3 text-studio-muted">A precise product photograph with controlled lighting...</span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="h-8 flex-1 rounded-md border border-studio-border bg-studio-card px-2 py-2 text-[8px] text-studio-ink">
                  1024 × 1024
                </div>
                <div className="flex h-8 items-center rounded-md bg-studio-accent px-3 text-[8px] font-semibold text-studio-accent-fg">
                  Generate
                </div>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-[1fr_0.8fr] gap-3">
              <div className="relative min-h-20 overflow-hidden rounded-lg bg-studio-card">
                <div className="absolute inset-3 rounded-md border border-studio-border bg-studio-card-hover" />
                <div className="absolute bottom-2 left-2 right-2 h-1 rounded-full bg-studio-border">
                  <div className="h-1 w-2/3 rounded-full bg-studio-accent" />
                </div>
              </div>
              <div className="rounded-lg border border-studio-border bg-studio-panel p-2.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-studio-success" />
                <p className="mt-2 text-[8px] font-semibold text-studio-ink">Ready</p>
                <p className="mt-1 text-[7px] leading-3 text-studio-muted">Provider and output settings are valid.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
